import { useLocation } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Users, Calendar, MapPin } from "lucide-react";
import unionChapter1 from "@/assets/union-chapter-1.jpg";
import unionChapter2 from "@/assets/union-chapter-2.jpg";
import washingtonChapter1 from "@/assets/washington-chapter-1.jpg";
import washingtonChapter2 from "@/assets/washington-chapter-2.jpg";

interface ChapterInfo {
  name: string;
  location: string;
  description: string;
  images: [string, string];
}

const chapterData: Record<string, ChapterInfo> = {
  washington: {
    name: "Washington",
    location: "Washington, MO",
    description: "The Washington chapter is our founding location, serving the greater Washington, Missouri area. Join our vibrant community of local business professionals committed to helping each other grow through trusted referrals and genuine connections.",
    images: [washingtonChapter1, washingtonChapter2]
  },
  union: {
    name: "Union",
    location: "Union, MO",
    description: "The Union chapter expands our network to serve the Union, Missouri community. We're building a new group of dedicated professionals ready to connect, collaborate, and grow together.",
    images: [unionChapter1, unionChapter2]
  }
};

const ChapterHome = () => {
  const location = useLocation();
  const chapterSlug = location.pathname.split('/')[1]; // 'washington' or 'union'
  const chapter = chapterData[chapterSlug] || chapterData.washington;

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary/10 via-background to-accent/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4 animate-fade-in">
              Welcome to RCN {chapter.name}
            </h1>
            <p className="text-xl text-muted-foreground mb-2 animate-fade-in" style={{ animationDelay: "0.1s" }}>
              <MapPin className="inline-block w-5 h-5 mr-1" />
              {chapter.location}
            </p>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto animate-fade-in" style={{ animationDelay: "0.2s" }}>
              {chapter.description}
            </p>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="hover:shadow-xl transition-shadow animate-fade-in flex flex-col" style={{ animationDelay: "0.1s" }}>
                <CardContent className="p-8 text-center flex flex-col flex-1">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold mb-3">Our Members</h2>
                  <p className="text-muted-foreground mb-6 flex-1">
                    Explore our diverse community of local business professionals and discover potential partners for your business growth.
                  </p>
                  <Button size="lg" asChild className="w-full mt-auto">
                    <a href={`/${chapterSlug}/members`}>View Members</a>
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-xl transition-shadow animate-fade-in flex flex-col" style={{ animationDelay: "0.2s" }}>
                <CardContent className="p-8 text-center flex flex-col flex-1">
                  <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Calendar className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold mb-3">Upcoming Events</h2>
                  <p className="text-muted-foreground mb-6 flex-1">
                    Check out our networking meetings, special events, and opportunities to connect with fellow members.
                  </p>
                  <Button size="lg" asChild className="w-full mt-auto">
                    <a href={`/${chapterSlug}/events`}>View Events</a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter Images */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted animate-fade-in" style={{ animationDelay: "0.1s" }}>
                <img
                  src={chapter.images[0]}
                  alt={`${chapter.name} chapter photo 1`}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden bg-muted animate-fade-in" style={{ animationDelay: "0.2s" }}>
                <img
                  src={chapter.images[1]}
                  alt={`${chapter.name} chapter photo 2`}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-primary to-primary/80 text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-fade-in">
            Ready to Join {chapter.name}?
          </h2>
          <p className="text-lg md:text-xl max-w-2xl mx-auto mb-8 opacity-95 animate-fade-in" style={{ animationDelay: "0.1s" }}>
            Visit one of our meetings and experience the power of trusted business referrals firsthand.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in" style={{ animationDelay: "0.2s" }}>
            <Button size="lg" variant="secondary" asChild>
              <a href="/visit-a-meeting">
                Visit a Meeting
              </a>
            </Button>
            <Button size="lg" variant="outline" className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary" asChild>
              <a href="/benefits">Learn More About Membership</a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ChapterHome;